import * as React from 'react'
import { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { ProjectGroupProps, SavedFilter, SavedFilterData } from '../types'
import I18n from '../../../../libs/i18n'
import FilterControl from './filter_control'
import { ASSOCIATION_OPTIONS_PROP_MAP, compactFilterData, isFilterableColumn } from './filter_utils'

interface SavedFilterModalProps {
  isOpen: boolean
  onClose: () => void
  filter: SavedFilter | null
  projectGroup: ProjectGroupProps
  currentFilterData: SavedFilterData
  onSave: (name: string, filter: SavedFilterData, isDefault: boolean) => Promise<void>
  onDelete: (id: number) => Promise<void>
}

export default function SavedFilterModal({
  isOpen,
  onClose,
  filter,
  projectGroup,
  currentFilterData,
  onSave,
  onDelete,
}: SavedFilterModalProps) {
  const [name, setName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [draft, setDraft] = useState<SavedFilterData>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isEditing = filter !== null

  useEffect(() => {
    if (isOpen) {
      if (filter) {
        setName(filter.name)
        setIsDefault(filter.is_default)
        setDraft(compactFilterData(filter.filter))
      } else {
        setName('')
        setIsDefault(false)
        setDraft(compactFilterData(currentFilterData))
      }
    }
  }, [isOpen, filter, currentFilterData])

  const handleSave = async () => {
    if (!name.trim()) return

    setIsSaving(true)
    try {
      const filterData = compactFilterData(draft)
      await onSave(name.trim(), filterData, isDefault)
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!filter) return

    setIsDeleting(true)
    try {
      await onDelete(filter.id)
      onClose()
    } finally {
      setIsDeleting(false)
    }
  }

  const handleFilterChange = (columnKey: string, value?: SavedFilterData[string]) => {
    setDraft((previousDraft) => {
      const nextDraft = { ...previousDraft }
      if (value == null) {
        delete nextDraft[columnKey]
      } else {
        nextDraft[columnKey] = value
      }
      return nextDraft
    })
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {isEditing
          ? I18n.t('project_groups.overview.saved_filters.modal.title_edit')
          : I18n.t('project_groups.overview.saved_filters.modal.title_new')}
      </ModalHeader>
      <ModalBody>
        <div className="form-group">
          <label htmlFor="filter-name">{I18n.t('project_groups.overview.saved_filters.modal.name_label')}</label>
          <input
            id="filter-name"
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <div className="form-check">
            <input
              id="filter-is-default"
              type="checkbox"
              className="form-check-input mr-2"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="filter-is-default">
              {I18n.t('project_groups.overview.saved_filters.modal.is_default_label')}
            </label>
          </div>
        </div>

        <div className="card" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <div className="card-body">
            {projectGroup.columns.filter(isFilterableColumn).map((column, index, array) => {
              const optionsProp = ASSOCIATION_OPTIONS_PROP_MAP[column.key]
              const options = optionsProp ? projectGroup[optionsProp] : undefined
              const isLast = index === array.length - 1

              return (
                <div className={isLast ? '' : 'form-group'} key={column.id}>
                  <div className="mb-2">{column.label}</div>
                  <FilterControl
                    column={column}
                    value={draft[column.key]}
                    options={options}
                    onChange={handleFilterChange}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {isEditing && (
          <button
            type="button"
            className="btn btn-outline-danger mr-auto"
            onClick={handleDelete}
            disabled={isDeleting || isSaving}
          >
            {I18n.t('project_groups.overview.saved_filters.modal.delete_button')}
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!name.trim() || isSaving || isDeleting}
        >
          {I18n.t('project_groups.overview.saved_filters.modal.save_button')}
        </button>
      </ModalFooter>
    </Modal>
  )
}
